import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import { flatten as _flatten, omit as _omit, uniqBy as _uniqBy } from 'lodash/fp'

import {
  EMPTY,
  Observable,
  catchError,
  concat,
  filter,
  forkJoin,
  lastValueFrom,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs'

import {
  ActionTarget,
  ContextItem,
  ConversationOutcomeClassification,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  AIContentType,
  ContextDataKeys,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ConversationErrorActions } from '@flowx/designer/modules/ai/conversation/conversation-error.actions'
import {
  BulkDataModelAttribute,
  DataModelAttributeType,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { UiDesignerContext } from '@flowx/designer/modules/ui-designer/models/ui-designer-context.enum'
import {
  FormFieldsWithCurrencyCode,
  allowedFormComponents,
} from '@flowx/designer/modules/ui-designer/services/templateConfig.entity'
import { DataModelApiService } from '@flowx/designer/shared/api/data-model.api'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import { ProcessDataModelApiService } from '@flowx/designer/shared/api/process-data-model.api'
import { ReusableUiTemplateDataModelApiService } from '@flowx/designer/shared/api/reusable-ui-template-data-model.api'
import { TemplateConfigApiService } from '@flowx/designer/shared/api/template-config.api'
import {
  AppNavigationService,
  NavigationActions,
} from '@flowx/designer/shared/app-navigation/app-navigation.service'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'
import { UpdateTemplateConfigKey } from '@flowx/designer/shared/entities/data-model.entity'
import { DataModelAutocompleteService } from '@flowx/designer/shared/services/data-model-autocomplete.service'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import { ReusableUiTemplateDataModelActions } from '@flowx/designer/shared/store/actions/reusable-ui-template-data-model.actions'
import { TemplateConfigActions } from '@flowx/designer/shared/store/actions/template-config.actions'
import {
  selectCurrentResourceId,
  selectDataModelEntitiesArray,
  selectProjectDataModelEntitiesArray,
  selectResourceDataModelEntities,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { nodesSelector, selectedNode } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import {
  selectReusableUiTemplateResourceId,
  selectUiDesignerContext,
  templateConfigFeature,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import {
  ComponentIdentifierEnum,
  FormFieldsEnum,
  TemplateConfigInterface,
  TemplateConfigSaveStatus,
  TemplateConfigValidationStatus,
} from '@flowx/shared/interfaces'
import { RxJsUtils } from '@flowx/shared/utils'

import { AiDesignerActions } from './designer.actions'

import { ConversationApiService } from '../../../common/api/conversation-api.service'
import { AiConversationContextBuilderService } from '../../../conversation/ai-conversation-context-builder.service'
import { AiConversationService } from '../../../conversation/ai-conversation.service'
import {
  ConversationActions,
  CtaComponentActions,
  TypeAreaActions,
} from '../../../conversation/conversation.actions'
import { ConversationSelectors } from '../../../conversation/conversation.selectors'
import { AiDesignerService } from '../../../store/ai-designer.service'
import { AiActions, AiConversationServiceActions } from '../../../store/ai.actions'
import { DataModelDraft, DataModelDraftItem, MessageType } from '../../../types/ai.types'
import { DataModelItemStatus } from '../dictionary/designer.dictionary'
import { DesignerService } from '../services/designer.service'

@Injectable()
export class AiDesignerEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #templateConfigApi = inject(TemplateConfigApiService)
  #dataModelAutocompleteService = inject(DataModelAutocompleteService)
  #dataModelBuilderApiService = inject(ProcessDataModelApiService)
  #dataModelApiService = inject(DataModelApiService)
  #reusableTemplateDataModelApiService = inject(ReusableUiTemplateDataModelApiService)
  #nodeApi = inject(NodeApiService)
  #aiDesignerService = inject(AiDesignerService)
  #aiConversationService = inject(AiConversationService)
  #conversationApiService = inject(ConversationApiService)
  #appNavigationService = inject(AppNavigationService)
  #contextBuilder = inject(AiConversationContextBuilderService)

  #selectedNode = this.#store.selectSignal(selectedNode)

  aiGenerateInNodeClicked = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDesignerActions.aiGenerateInNodeClicked),
        withLatestFrom(
          this.#store.select(ConversationSelectors.selectedProcessId),
          this.#store.select(selectReusableUiTemplateResourceId),
          this.#store.select(selectProjectDataModelEntitiesArray)
        ),
        tap(() => this.#store.dispatch(CtaComponentActions.generateUiCtaClick())),
        switchMap(
          ([{ templateConfig }, processId, reusableTemplateResourceId, projectDataModel]) => {
            if (processId) {
              const nodes = this.#store.selectSignal(nodesSelector)()
              const node =
                nodes.find((n) => n.id === templateConfig.nodeDefinitionId) ?? this.#selectedNode()

              if (!node) {
                return EMPTY
              }

              return this.#generateUiHintedAction({
                subject: 'node',
                agentAction: AgentAction.CreateUi,
                target: {
                  name: node?.name ?? '',
                  targetType: ConversationThreadExecutionPlanActionTargetType.Node,
                  node: {
                    id: node?.id.toString(),
                    nodeType: node?.type || '',
                    navigationAreaId: templateConfig.navigationAreaId?.toString(),
                    templateConfigId: templateConfig.id?.toString(),
                  },
                },
              })
            }

            if (reusableTemplateResourceId) {
              return this.#generateUiHintedAction({
                subject: 'reusable template',
                agentAction: AgentAction.CreateUiTemplate,
                target: {
                  name: templateConfig.customIdentifier || templateConfig.componentIdentifier,
                  targetType: ConversationThreadExecutionPlanActionTargetType.ReusableUiTemplate,
                },
                contextItems: [
                  {
                    key: ContextDataKeys.DesignerDataModel,
                    itemType: ConversationThreadContextDataItemType.GenericString,
                    valueGenericString: JSON.stringify(projectDataModel),
                  },
                  {
                    key: ContextDataKeys.UiTemplateResource,
                    itemType: ConversationThreadContextDataItemType.GenericString,
                    valueGenericString: JSON.stringify({
                      resourceId: reusableTemplateResourceId,
                      templateConfigId: templateConfig.id?.toString(),
                    }),
                  },
                ],
              })
            }

            return EMPTY
          }
        )
      ),
    { dispatch: false }
  )

  generateUiFromNodeAction = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDesignerActions.generateUiFromNodeAction),
        map(() => this.#selectedNode()),
        withLatestFrom(this.#store.select(selectDataModelEntitiesArray)),
        switchMap(([node, dataModelEntities]) =>
          this.#contextBuilder
            .buildProjectDataModelContextItem()
            .pipe(map((projectDataModelItem) => ({ node, dataModelEntities, projectDataModelItem })))
        ),
        switchMap(({ node, dataModelEntities, projectDataModelItem }) =>
          this.#aiConversationService
            .implementHintedAction({
              message: `Hello! I will assist you in creating consistent, and modern UIs tailored to your project's needs. Let's start designing your interface.`,
              context: {
                items: [
                  {
                    key: ContextDataKeys.UiAiContentType,
                    itemType: ConversationThreadContextDataItemType.GenericString,
                    valueGenericString: AIContentType.Ui,
                  },
                  {
                    key: ContextDataKeys.DesignerDataModel,
                    itemType: ConversationThreadContextDataItemType.GenericString,
                    valueGenericString: JSON.stringify(dataModelEntities),
                  },
                  ...(projectDataModelItem ? [projectDataModelItem] : []),
                ],
              },
              userPromptRequired: true,
              target: {
                name: node?.name || '',
                targetType: ConversationThreadExecutionPlanActionTargetType.Node,
                node: {
                  id: node?.id.toString(),
                  nodeType: node?.type || '',
                  navigationAreaId: node?.navigationAreaId?.toString(),
                },
              },
              agent: { agent: AgentName.AiDesigner, action: AgentAction.CreateUi },
            })
            .pipe(
              take(1),
              catchError((err) => {
                const error =
                  err instanceof Error ? err : new Error('Failed to generate UI from node action')
                console.error('[Error] Generate UI from node action failed:', error)
                return of(ConversationErrorActions.generateUiFromNodeActionFailed({ error }))
              })
            )
        )
      ),
    { dispatch: false }
  )

  keepOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepOutcome),
      withLatestFrom(
        this.#store.select(ConversationSelectors.hasUiOutcome),
        this.#store.select(ConversationSelectors.isUiOutcomeFinished)
      ),
      filter(([, hasUiOutcome]) => !!hasUiOutcome),
      switchMap(([, , isUiFinished]) => {
        if (isUiFinished) {
          return of(AiDesignerActions.keepDataModelDraft())
        }

        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()

        return this.#aiDesignerService.keepTemplateConfigDraft().pipe(
          map(() => AiDesignerActions.keepOutcomeSuccess()),
          catchError((err) => {
            const error = err instanceof Error ? err : new Error('Failed to keep designer outcome')
            console.error('[Conversation Error] Keep designer outcome failed:', error)
            return of(
              ConversationErrorActions.keepOutcomeFailed({
                error,
                threadId,
                conversationId,
              })
            )
          })
        )
      })
    )
  )

  discardOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.discardOutcome),
      map(() => AiDesignerActions.discardOutcomeSuccess())
    )
  )

  updateDataModelOnLastMessage = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.updateDataModelOnLastMessage),
      withLatestFrom(
        this.#store.select(ConversationSelectors.activeConversationId),
        this.#store.select(ConversationSelectors.activeAgentThreadId),
        this.#store.select(ConversationSelectors.latestAgentMessageWithAgentAction)
      ),
      switchMap(([{ dataModel }, conversationId, threadId, messageWithAgentAction]) => {
        if (!conversationId || !threadId || !messageWithAgentAction?.id) {
          console.warn(
            '[AiDesignerEffects] Cannot update data model: missing conversationId, threadId, or messageId'
          )
          return EMPTY
        }

        const generatedDataModelItem = {
          key: ContextDataKeys.GeneratedDataModel,
          itemType: ConversationThreadContextDataItemType.GenericString,
          valueGenericString: JSON.stringify(dataModel),
        }

        const context = {
          items: _uniqBy('key', [
            generatedDataModelItem,
            ...(messageWithAgentAction.contextData?.items ?? []),
          ]),
        }

        return this.#conversationApiService
          .patchMessageContext(conversationId, threadId, messageWithAgentAction.id, { context })
          .pipe(
            map(() =>
              ConversationActions.patchMessageContextItemsApiResponse({
                threadId,
                messageId: messageWithAgentAction.id,
                context,
              })
            ),
            catchError((err) => {
              const error =
                err instanceof Error ? err : new Error('Failed to update data model on message')
              console.error('[Error] Update data model on message failed:', error)
              return of(
                ConversationErrorActions.updateDataModelFailed({
                  error,
                  conversationId,
                  threadId,
                  messageId: messageWithAgentAction.id,
                })
              )
            })
          )
      })
    )
  )

  keepDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepDataModelDraft),
      withLatestFrom(
        this.#store
          .select(ConversationSelectors.aiGeneratedUiDataModelChanges)
          .pipe(
            map(
              (generatedUIDataModelChanges) =>
                !!generatedUIDataModelChanges?.new?.some(
                  (dataModelItem) => !dataModelItem.inactive
                ) ||
                !!generatedUIDataModelChanges?.referenced?.some(
                  (dataModelItem) => !dataModelItem.inactive
                )
            )
          )
      ),
      map(([, hasItemsToSave]) =>
        hasItemsToSave
          ? AiDesignerActions.saveDataModelDraft()
          : AiDesignerActions.linkTemplateConfigKeys()
      )
    )
  )

  saveDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveDataModelDraft),
      withLatestFrom(this.#store.select(ConversationSelectors.aiGeneratedUiDataModelChanges)),
      map(([, dataModelChanges]) => {
        const bulkDataModelAttributes = DesignerService.prepareDataModelForBulkCreate([
          ...dataModelChanges.new.map((dm) => ({ ...dm, state: DataModelItemStatus.CREATED })),
          ...dataModelChanges.existing.map((dm) => ({ ...dm, state: DataModelItemStatus.MAPPED })),
        ]).map(
          ({ key, type, label, multiInstance }) =>
            ({
              path: key,
              type,
              description: label,
              multiInstance,
            }) satisfies BulkDataModelAttribute
        )

        // Referenced items can't go through the bulk path-attribute endpoint (it can't express an
        // OBJECT reference); apply them via addAttributeToDataModelEntity with the reference arm.
        const referencedItems = (dataModelChanges.referenced ?? []).filter((dm) => !dm.inactive)

        return { bulkDataModelAttributes, referencedItems }
      }),
      withLatestFrom(
        this.#store.select(selectCurrentResourceId),
        this.#store.select(selectUiDesignerContext),
        this.#store.select(selectResourceDataModelEntities)
      ),
      switchMap(
        ([
          { bulkDataModelAttributes, referencedItems },
          resourceId,
          uiDesignerContext,
          resourceEntities,
        ]) => {
          // Reusable-template UIs store their data model on the template, not the process, so route
          // the bulk path-attribute create to the reusable-template endpoint in that context.
          const isReusableTemplate = uiDesignerContext === UiDesignerContext.ReusableUiTemplate

          const bulkSave$: Observable<unknown> = bulkDataModelAttributes.length
            ? isReusableTemplate
              ? this.#reusableTemplateDataModelApiService.bulkAddAttributesFromPath(
                  resourceId,
                  bulkDataModelAttributes
                )
              : this.#dataModelBuilderApiService.bulkAddAttributesFromPath(
                  resourceId,
                  bulkDataModelAttributes
                )
            : of(null)

          // Project/library OBJECT references live on the process data model; skip in the
          // reusable-template context (its data model has no process root entity).
          const referencedSave$: Observable<unknown> =
            referencedItems.length && !isReusableTemplate
              ? this.#applyReferencedDataModelItems(resourceId, resourceEntities, referencedItems)
              : of(null)

          // Primitives first, then references (which depend on the process root entity existing).
          return concat(bulkSave$, referencedSave$).pipe(
            toArray(),
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map(() => AiDesignerActions.saveDataModelDraftSuccess()),
            catchError(({ status, error }) =>
              of(
                AiDesignerActions.saveDataModelDraftFailure({
                  statusCode: status,
                  errorMsg: error,
                })
              )
            )
          )
        }
      )
    )
  )

  onSaveDataModelDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveDataModelDraftSuccess),
      map(() => AiDesignerActions.reloadDataModel())
    )
  )

  reloadDataModel = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.reloadDataModel),
      withLatestFrom(
        this.#store.select(selectCurrentResourceId),
        this.#store.select(selectUiDesignerContext)
      ),
      tap(([, resourceId, uiDesignerContext]) =>
        this.#store.dispatch(
          uiDesignerContext === UiDesignerContext.ReusableUiTemplate
            ? ReusableUiTemplateDataModelActions.loadEntities({ resourceId })
            : ProcessDataModelActions.loadEntities({ resourceId })
        )
      ),
      switchMap(([, , uiDesignerContext]) =>
        this.#actions$.pipe(
          ofType(
            uiDesignerContext === UiDesignerContext.ReusableUiTemplate
              ? ReusableUiTemplateDataModelActions.loadEntitiesSuccess
              : ProcessDataModelActions.loadEntitiesSuccess
          ),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          map(() => AiDesignerActions.reloadDataModelSuccess())
        )
      )
    )
  )

  onReloadDataModelSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.reloadDataModelSuccess),
      map(() => AiDesignerActions.linkTemplateConfigKeys())
    )
  )

  linkTemplateConfigKeys = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.linkTemplateConfigKeys),
      withLatestFrom(
        this.#store.select(ConversationSelectors.userTaskNavigationAreaId),
        this.#store.select(ConversationSelectors.aiGeneratedUiDataModelChanges),
        this.#store.select(selectUiDesignerContext),
        this.#store.select(selectReusableUiTemplateResourceId)
      ),
      tap(
        async ([, areaId, generatedUIDataModelChanges, uiDesignerContext, reusableResourceId]) => {
          const isKeyLinkable = (dataModel: DataModelDraftItem): boolean =>
            allowedFormComponents.includes(dataModel.templateConfigIdentifier as FormFieldsEnum)

          const isTableSource = (dataModel: DataModelDraftItem): boolean =>
            dataModel.templateConfigIdentifier === ComponentIdentifierEnum.TABLE

          const isTextInterpolating = (dataModel: DataModelDraftItem): boolean =>
            !isKeyLinkable(dataModel) && !isTableSource(dataModel)

          const allDataModelChanges = [
            ...(generatedUIDataModelChanges?.new ?? []),
            ...(generatedUIDataModelChanges?.existing ?? []),
          ]

          const keyLinkableDataModel = allDataModelChanges.filter(isKeyLinkable)
          const textInterpolatingDataModel = allDataModelChanges.filter(isTextInterpolating)
          const tableSourceDataModel = allDataModelChanges.filter(isTableSource)

          const templateConfig = await RxJsUtils.getPreviousValue(
            this.#store.select(templateConfigFeature).pipe(
              map((state) => {
                const templateConfigsMap = state[areaId]?.entities?.templateConfigs
                const templateConfigs = templateConfigsMap ? Object.values(templateConfigsMap) : []
                const filteredTemplateConfigs = templateConfigs.filter((tc) => tc.id >= 0)

                return filteredTemplateConfigs
              })
            )
          )

          const keysUpdatePayload: UpdateTemplateConfigKey[] = keyLinkableDataModel.map(
            (dataModel) => {
              const key =
                FormFieldsWithCurrencyCode.includes(
                  dataModel.templateConfigIdentifier as FormFieldsEnum
                ) && dataModel.type === DataModelAttributeType.CURRENCY
                  ? `${dataModel.key}.code`
                  : dataModel.key

              return {
                id: dataModel.templateConfigId,
                ...(dataModel.inactive
                  ? {
                      key: '',
                      referencePath: null,
                    }
                  : {
                      key,
                      referencePath: this.#dataModelAutocompleteService.getRefPathFromNamePath(key),
                    }),
              }
            }
          )

          const templateConfigWithSources: TemplateConfigInterface[] =
            DesignerService.updateTemplateConfigPropertiesWithDataModelChanges(
              templateConfig,
              textInterpolatingDataModel,
              tableSourceDataModel
            )

          if (templateConfigWithSources.length > 0) {
            /**
             * For these data model items, we don't want to try to link them with the
             * link keys endpoint. That would give an error.
             * We will just update the template config properties.
             */
            const updatedTemplateConfigs = await lastValueFrom(
              this.#templateConfigApi.bulkUpdateTemplateConfigProperties(templateConfigWithSources)
            ).catch(({ status, error }) => {
              this.#store.dispatch(
                AiDesignerActions.linkTemplateConfigKeysFailure({ statusCode: status, errorMsg: error })
              )
              return null
            })

            if (!updatedTemplateConfigs) {
              return
            }

            this.#store.dispatch(
              /**
               * Because we called the bulkUpdateTemplateConfigProperties endpoint directly,
               * not using the NGRX effect, we need to dispatch this action manually.
               */
              TemplateConfigActions.bulkUpdateTemplateConfigsSuccess({
                updatedTemplateConfigs: updatedTemplateConfigs.map((tc) => {
                  const { templateConfig, ...rest } = _omit(
                    ['templateConfigs', 'misconfigurations'],
                    tc
                  )
                  return {
                    ...rest,
                    templateConfig: templateConfig?.map((child) => child.id) || [],
                    meta: {
                      saveStatus: TemplateConfigSaveStatus.SAVED,
                      validationStatus: TemplateConfigValidationStatus.VALID,
                    },
                  }
                }),
              })
            )

            if (keyLinkableDataModel.length === 0) {
              /**
               * If there are no key linkable data model items, then we can skip the
               * next switchMap and jump directly to the linkTemplateConfigKeysSuccess action.
               *
               * If we don't do this, when there are no key linkable data model items,
               * the effect will wait for the updateTemplateConfigKeySuccess action,
               * that will never arrive and the effect will never complete. We will remain
               * in a pending state.
               */
              this.#store.dispatch(AiDesignerActions.linkTemplateConfigKeysSuccess())
            }
          }

          if (keyLinkableDataModel.length > 0) {
            /**
             * For these data model items, we want to try to link them with the
             * link keys endpoint. This will also jump to the next switchMap
             * when the action is completed. Then, the next switchMap will
             * dispatch the AiDesignerActions.linkTemplateConfigKeysSuccess action.
             */
            this.#store.dispatch(
              TemplateConfigActions.updateTemplateConfigKey({
                // The template-config store groups reusable templates under their
                // resource id, not a navigation area, so key the update accordingly.
                id:
                  uiDesignerContext === UiDesignerContext.ReusableUiTemplate
                    ? reusableResourceId
                    : areaId,
                payload: keysUpdatePayload,
              })
            )
          }
        }
      ),
      switchMap(([, , , uiDesignerContext]) =>
        this.#actions$.pipe(
          ofType(TemplateConfigActions.updateTemplateConfigKeySuccess),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          switchMap(() => {
            // autoLink is process-only; reusable templates have no equivalent, so
            // skip straight to reloading the template in that context.
            if (uiDesignerContext === UiDesignerContext.ReusableUiTemplate) {
              return this.#reloadReusableTemplateAndComplete()
            }

            this.#store.dispatch(ProcessDataModelActions.autoLinkRequested())

            return this.#actions$.pipe(
              ofType(ProcessDataModelActions.autoLinkSuccess),
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              take(1),
              switchMap(() => this.#reloadUserTaskTemplateConfigsAndComplete()),
              catchError(({ status, error }) =>
                of(
                  AiDesignerActions.linkTemplateConfigKeysFailure({
                    statusCode: status,
                    errorMsg: error,
                  })
                )
              )
            )
          })
        )
      )
    )
  )

  onLinkTemplateConfigKeysSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.linkTemplateConfigKeysSuccess),
      map(() => AiDesignerActions.keepDataModelDraftSuccess())
    )
  )

  discardDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.discardDataModelDraft),
      withLatestFrom(
        this.#store.select(ConversationSelectors.userTaskNavigationAreaId),
        this.#store.select(ConversationSelectors.aiGeneratedUiDataModelChanges),
        this.#store.select(selectUiDesignerContext),
        this.#store.select(selectReusableUiTemplateResourceId)
      ),
      switchMap(
        ([, areaId, generatedUIDataModelChanges, uiDesignerContext, reusableResourceId]) => {
          const updateTemplateConfigKeys = [
            ...generatedUIDataModelChanges.existing,
            ...generatedUIDataModelChanges.new,
          ].map(({ templateConfigId }) => ({
            id: templateConfigId,
            key: '',
            referencePath: null,
          }))

          return of(
            TemplateConfigActions.updateTemplateConfigKey({
              id:
                uiDesignerContext === UiDesignerContext.ReusableUiTemplate
                  ? reusableResourceId
                  : areaId,
              payload: updateTemplateConfigKeys,
            }),
            AiDesignerActions.discardDataModelDraftSuccess()
          )
        }
      )
    )
  )

  aiDesignerPromptSubmit = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(TypeAreaActions.promptSubmit),
        withLatestFrom(
          this.#store.select(ConversationSelectors.currentMessageAgent),
          this.#store.select(ConversationSelectors.activeAgentTarget),
          this.#store.select(ConversationSelectors.selectedProcessId)
        ),
        filter(([, agent, target]) => agent === AgentName.AiDesigner && target.targetType != null),
        switchMap(([, , target, selectedProcessId]) => {
          const nodes = this.#store.selectSignal(nodesSelector)()
          const node = nodes.find((node) => node.id === parseInt(target.node.id, 10))
          if (!node) {
            return EMPTY
          }

          const appId = this.#store.selectSignal(selectAppId)()
          const appVersionUuid = this.#store.selectSignal(selectAppVersionId)()

          return this.#appNavigationService.execute({
            id: NavigationActions.openNode,
            data: {
              nodeId: node.id,
              processId: selectedProcessId,
              processVersionUuid: selectedProcessId,
              appId,
              appVersionUuid,
              navigationAreaId: node.navigationAreaId,
              swimlaneId: node.swimlaneDefinitionId,
            },
          })
        })
      ),
    { dispatch: false }
  )

  generateUiStreamEnded = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiConversationServiceActions.conversationMessageStreamSuccess),
      withLatestFrom(this.#store.select(ConversationSelectors.activeAgentThread)),
      map(([data, activeThread]) => {
        const message = activeThread?.messages.find((message) => message.id === data.messageId)
        const agent = message?.executionPlan.actions?.[0]?.agent.agent

        return {
          activeThread,
          message,
          agent,
        }
      }),
      filter(
        ({ agent, message }) =>
          agent === AgentName.AiDesigner &&
          message?.outcome?.outcomes.some(
            (outcome) => outcome.outcomeClassification === ConversationOutcomeClassification.Main
          )
      ),
      withLatestFrom(
        this.#store.select(ConversationSelectors.hasUiOutcome),
        this.#store.select(ConversationSelectors.uiOutcomeParsingError),
        this.#store.select(ConversationSelectors.errorOutcomeDetails),
        this.#store.select(ConversationSelectors.latestAgentMessageWithAgentAction),
        this.#store.select(ConversationSelectors.activeAgentThreadId)
      ),
      switchMap(([, hasUiOutcome, parsingError, errorDetails, latestMessage, threadId]) => {
        if (errorDetails?.hasError) {
          return EMPTY
        }

        if (hasUiOutcome) {
          const messageText =
            'UI has been generated. You can tweak the results by telling me what to change.'
          return this.#aiConversationService
            .addStoreMessage({
              prompt: messageText,
              type: MessageType.SystemMsg,
            })
            .pipe(switchMap(() => EMPTY))
        }

        if (parsingError) {
          return of(
            ConversationErrorActions.agentOutcomeParsingFailed({
              error: parsingError,
              agentName: AgentName.AiDesigner,
              messageId: latestMessage?.id,
              threadId,
            })
          )
        }

        return EMPTY
      })
    )
  )

  #generateUiHintedAction(params: {
    subject: string
    agentAction: AgentAction
    target: ActionTarget
    contextItems?: ContextItem[]
  }): Observable<unknown> {
    return this.#contextBuilder.buildProjectDataModelContextItem().pipe(
      switchMap((projectDataModelItem) =>
        this.#aiConversationService.implementHintedAction({
          message: 'I am here to support you in generating UI in this area.',
          context: {
            items: [
              {
                key: ContextDataKeys.UiAiContentType,
                itemType: ConversationThreadContextDataItemType.GenericString,
                valueGenericString: AIContentType.Ui,
              },
              ...(params.contextItems ?? []),
              ...(projectDataModelItem ? [projectDataModelItem] : []),
            ],
          },
          userPromptRequired: true,
          target: params.target,
          agent: { agent: AgentName.AiDesigner, action: params.agentAction },
        })
      ),
      take(1),
      catchError((err) => {
        const error =
          err instanceof Error ? err : new Error(`Failed to generate UI in ${params.subject}`)
        console.error(`[Error] Generate UI in ${params.subject} failed:`, error)
        return EMPTY
      })
    )
  }

  #reloadUserTaskTemplateConfigsAndComplete(): Observable<Action> {
    return this.#store.select(ConversationSelectors.userTaskNavigationAreaId).pipe(
      take(1),
      tap((areaId) =>
        this.#store.dispatch(
          TemplateConfigActions.loadUserTaskTemplateConfigs({
            navigationAreaIds: [areaId],
          })
        )
      ),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(TemplateConfigActions.loadUserTaskTemplateConfigsSuccess),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          map(() => AiDesignerActions.linkTemplateConfigKeysSuccess())
        )
      ),
      catchError(({ status, error }) =>
        of(
          AiDesignerActions.linkTemplateConfigKeysFailure({
            statusCode: status,
            errorMsg: error,
          })
        )
      )
    )
  }

  #reloadReusableTemplateAndComplete(): Observable<Action> {
    return this.#store.select(selectReusableUiTemplateResourceId).pipe(
      take(1),
      tap((resourceId) =>
        this.#store.dispatch(TemplateConfigActions.loadReusableTemplate({ id: resourceId }))
      ),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(TemplateConfigActions.loadReusableTemplateSuccess),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          map(() => AiDesignerActions.linkTemplateConfigKeysSuccess())
        )
      ),
      catchError(({ status, error }) =>
        of(
          AiDesignerActions.linkTemplateConfigKeysFailure({
            statusCode: status,
            errorMsg: error,
          })
        )
      )
    )
  }

  #extractLinkKeyPayloadToUpdate(
    templateConfig: TemplateConfigInterface,
    dataModelKeysToUpdate: string[],
    draftDataModel: DataModelDraft
  ): UpdateTemplateConfigKey[] {
    const hasUpdatingKey = templateConfig.key && dataModelKeysToUpdate.includes(templateConfig.key)
    const updatingKey =
      draftDataModel.existing.find(
        ({ templateConfigMappedKey }) => templateConfigMappedKey === templateConfig.key
      )?.key || draftDataModel.new.find(({ key }) => key === templateConfig.key)?.key

    let currKeyPayload: UpdateTemplateConfigKey[] = []
    if (hasUpdatingKey) {
      const keyRef = this.#dataModelAutocompleteService.getRefPathFromNamePath(updatingKey)

      if (keyRef) {
        currKeyPayload = [{ id: templateConfig.id, key: updatingKey, referencePath: keyRef }]
      }
    }

    const childrenPayloads: UpdateTemplateConfigKey[] =
      _flatten(
        templateConfig.templateConfig?.map((child) =>
          this.#extractLinkKeyPayloadToUpdate(child, dataModelKeysToUpdate, draftDataModel)
        )
      ) ?? []

    return [...currKeyPayload, ...childrenPayloads]
  }

  // Ensure the process root entity exists, then add each referenced item as an OBJECT attribute
  // carrying the `{ targetResourceDefinitionId, appId }` reference arm.
  #applyReferencedDataModelItems(
    resourceId: string,
    resourceEntities: { identifier?: string; root?: boolean }[],
    referencedItems: DataModelDraftItem[]
  ): Observable<unknown> {
    const options = { resourceType: ApplicationResourceType.Process, resourceId }

    type EntityNode = {
      identifier?: string
      root?: boolean
      flowxUuid?: string
      attributes?: { name?: string; reference?: { targetFlowxUuid?: string } }[]
    }
    const entities = resourceEntities as EntityNode[]
    const rootEntity = entities.find((entity) => entity.root)

    // A reference key carries the object's full path (e.g. application.client). Resolve the entity it
    // must be created under by walking the parent segments from root, following each OBJECT attribute's
    // targetFlowxUuid to its child entity, so application.client lands inside the "application" object
    // rather than as a sibling of it at the root. Falls back to root when a segment can't be resolved.
    const resolveTargetEntityId = (key: string | undefined, rootId: string): string => {
      const parents = (key ?? '').split('.').slice(0, -1)
      let entity: EntityNode | undefined = rootEntity
      for (const segment of parents) {
        const childUuid = entity?.attributes?.find((a) => a.name === segment)?.reference
          ?.targetFlowxUuid
        entity = childUuid ? entities.find((e) => e.flowxUuid === childUuid) : undefined
        if (!entity) {
          return rootId
        }
      }
      return entity?.identifier ?? rootId
    }

    const rootEntityId$: Observable<string> = rootEntity?.identifier
      ? of(rootEntity.identifier)
      : this.#dataModelApiService
          .addDataModelEntity(options, { name: '', root: true })
          .pipe(map((response) => mapResource(response).id))

    return rootEntityId$.pipe(
      switchMap((rootId) =>
        forkJoin(
          referencedItems.map((item) =>
            this.#dataModelApiService.addAttributeToDataModelEntity(
              options,
              resolveTargetEntityId(item.key, rootId),
              DesignerService.buildReferencedAttributeParams(item)
            )
          )
        )
      )
    )
  }
}
